import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import api from 'utils/api';

const EmployeeModal = ({
  isOpen,
  onClose,
  employee = null,
  onSave,
  availableServices = [],
  selectedSalon,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    employeeCode: '',
    services: [],
    status: 'available',
    workingHours: {
      start: '09:00',
      end: '18:00',
    },
    workingDays: [],
  });
  console.log('availableServices', availableServices);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const statusOptions = [
    { value: 'available', label: 'در دسترس' },
    { value: 'busy', label: 'مشغول' },
    { value: 'unavailable', label: 'غیرفعال' },
    { value: 'break', label: 'استراحت' },
  ];

  const serviceOptions = availableServices?.map((service) => ({
    value: service._id, // must be string
    label: service.name,
    description: `${service.duration} دقیقه - ${
      service.price?.toLocaleString('fa-IR') || 0
    } تومان`,
  }));

  const weekDays = [
    { value: 'saturday', label: 'شنبه' },
    { value: 'sunday', label: 'یکشنبه' },
    { value: 'monday', label: 'دوشنبه' },
    { value: 'tuesday', label: 'سه‌شنبه' },
    { value: 'wednesday', label: 'چهارشنبه' },
    { value: 'thursday', label: 'پنج‌شنبه' },
    { value: 'friday', label: 'جمعه' },
  ];

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee?.name || '',
        phone: employee?.phone || '',
        email: employee?.email || '',
        employeeCode: employee?.employeeCode || '',
        services: employee.services?.map(String) || [], // map ObjectId to string
        status: employee?.status || 'available',
        workingHours: employee?.workingHours || {
          start: '09:00',
          end: '18:00',
        },
        workingDays: employee?.workingDays || [],
      });
    } else {
      // Generate employee code for new employee
      const newCode = `EMP${Date.now()?.toString()?.slice(-6)}`;
      setFormData({
        name: '',
        phone: '',
        email: '',
        employeeCode: newCode,
        services: [],
        status: 'available',
        workingHours: { start: '09:00', end: '18:00' },
        workingDays: [
          'saturday',
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
        ],
      });
    }
    setErrors({});
  }, [employee, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleWorkingHoursChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      workingHours: {
        ...prev?.workingHours,
        [field]: value,
      },
    }));
  };

  const handleWorkingDayToggle = (day, checked) => {
    setFormData((prev) => ({
      ...prev,
      workingDays: checked
        ? [...prev?.workingDays, day]
        : prev?.workingDays?.filter((d) => d !== day),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'نام کارمند الزامی است';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'شماره تلفن الزامی است';
    } else if (!/^09\d{9}$/?.test(formData?.phone)) {
      newErrors.phone = 'شماره تلفن معتبر نیست';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'ایمیل الزامی است';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'ایمیل معتبر نیست';
    }

    if (formData?.services?.length === 0) {
      newErrors.services = 'حداقل یک خدمت باید انتخاب شود';
    }

    if (formData?.workingDays?.length === 0) {
      newErrors.workingDays = 'حداقل یک روز کاری باید انتخاب شود';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(
        `/salons/${selectedSalon}/employees`,
        formData
      );
      console.log('response', response);
      setFormData((prev) => [...prev, response.data]);
      //setNewEmployee({ name: '', role: '', services: [] }); // reset
    } catch (err) {
      console.error('Error adding employee:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-modal z-modal flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {employee ? 'ویرایش کارمند' : 'افزودن کارمند جدید'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}>
            <span className="sr-only">بستن</span>
          </Button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                اطلاعات پایه
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="نام کارمند"
                  type="text"
                  placeholder="نام کامل کارمند را وارد کنید"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  error={errors?.name}
                  required
                />

                <Input
                  label="کد کارمند"
                  type="text"
                  value={formData?.employeeCode}
                  onChange={(e) =>
                    handleInputChange('employeeCode', e?.target?.value)
                  }
                  disabled={!!employee}
                  description={
                    employee
                      ? 'کد کارمند قابل تغییر نیست'
                      : 'کد خودکار تولید شده'
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="شماره تلفن"
                  type="tel"
                  placeholder="09123456789"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  required
                />

                <Input
                  label="ایمیل"
                  type="email"
                  placeholder="example@email.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required
                />
              </div>

              <Select
                label="وضعیت"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange('status', value)}
                placeholder="وضعیت کارمند را انتخاب کنید"
              />
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                خدمات تخصصی
              </h3>

              <Select
                label="خدمات"
                description="خدماتی که این کارمند می‌تواند ارائه دهد"
                options={serviceOptions}
                value={formData?.services}
                onChange={(value) => handleInputChange('services', value)}
                error={errors?.services}
                multiple
                searchable
                placeholder="خدمات مورد نظر را انتخاب کنید"
                required
              />
            </div>

            {/* Working Hours */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                ساعات کاری
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="شروع کار"
                  type="time"
                  value={formData?.workingHours?.start}
                  onChange={(e) =>
                    handleWorkingHoursChange('start', e?.target?.value)
                  }
                />

                <Input
                  label="پایان کار"
                  type="time"
                  value={formData?.workingHours?.end}
                  onChange={(e) =>
                    handleWorkingHoursChange('end', e?.target?.value)
                  }
                />
              </div>
            </div>

            {/* Working Days */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                روزهای کاری
              </h3>

              {errors?.workingDays && (
                <p className="text-sm text-error">{errors?.workingDays}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {weekDays?.map((day) => (
                  <Checkbox
                    key={day?.value}
                    label={day?.label}
                    checked={formData?.workingDays?.includes(day?.value)}
                    onChange={(e) =>
                      handleWorkingDayToggle(day?.value, e?.target?.checked)
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}>
              انصراف
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              iconName="Save"
              iconPosition="left">
              {employee ? 'ذخیره تغییرات' : 'افزودن کارمند'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeModal;
